import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Check,
  Lightbulb,
  Palette,
  Tag as TagIcon,
  Megaphone,
  PiggyBank,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Sparkles,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_db2be5c0-fcfc-48d7-8252-f22b445a2705/artifacts/uqf9sd9c_LOGO%20FOR%20MINI%20MAKERS.png";

const COLORS = {
  purple: "#5B2A86",
  purpleDeep: "#3F1A66",
  gold: "#F5C518",
  orange: "#F2762E",
  pink: "#E91E80",
  green: "#3FA437",
  teal: "#0FA9A0",
  blue: "#1F8BD9",
  cream: "#FAF6EC",
};

const CAMPER_STEPS = [
  {
    icon: Lightbulb,
    title: "Think & Plan",
    sub: "Big ideas start here!",
    color: COLORS.gold,
  },
  {
    icon: Palette,
    title: "Create Products",
    sub: "Hands-on creative fun!",
    color: COLORS.orange,
  },
  {
    icon: TagIcon,
    title: "Brand Their Business",
    sub: "Design names, logos & more!",
    color: COLORS.green,
  },
  {
    icon: Megaphone,
    title: "Market Their Products",
    sub: "Learn how to share & sell!",
    color: COLORS.pink,
  },
  {
    icon: PiggyBank,
    title: "Sell with Confidence",
    sub: "Build skills for life!",
    color: COLORS.teal,
  },
];

const HIGHLIGHTS = [
  "Fun & Hands-on Workshops",
  "Branding & Packaging",
  "Market Day Showcase",
  "Business Basics for Kids",
  "Games, Prizes & More!",
];

const DETAIL_TILES = [
  { icon: Calendar, label: "Dates", value: "TBA", note: "Final schedule announced soon!", color: COLORS.purple },
  { icon: Clock, label: "Time", value: "TBA", note: "Final schedule announced soon!", color: COLORS.green },
  { icon: MapPin, label: "Location", value: "TBA", note: "Edmonton, Alberta · Final location announced soon!", color: COLORS.pink },
  { icon: DollarSign, label: "Investment", value: "TBA", note: "Details coming soon!", color: COLORS.blue },
];

const MiniMakersWaitlist = () => {
  const [formData, setFormData] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    num_children: 1,
    preferred_location: "",
    notes: "",
    agree_updates: false,
  });
  const [children, setChildren] = useState([{ first_name: "", age: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Mini Makers Market Camp Waitlist | Mix & Mingle Events";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Join the priority waitlist for Mini Makers Market Camp by Mix & Mingle Events — a creative summer experience where kids ages 4–16 turn ideas into real products, build brands, and showcase like pros."
    );
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleNumChildrenChange = (e) => {
    const count = parseInt(e.target.value, 10) || 1;
    setFormData((prev) => ({ ...prev, num_children: count }));
    setChildren((prev) => {
      const next = [...prev];
      if (count > next.length) {
        while (next.length < count) next.push({ first_name: "", age: "" });
      } else if (count < next.length) {
        next.length = count;
      }
      return next;
    });
  };

  const handleChildChange = (index, field, value) => {
    setChildren((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const scrollToForm = () => {
    document.getElementById("waitlist-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const firstChildName = children[0]?.first_name?.trim();
    if (
      !formData.parent_first_name ||
      !formData.email ||
      !firstChildName ||
      !formData.agree_updates
    ) {
      toast.error("Please fill in all required fields and agree to receive updates.");
      return;
    }
    // Ensure every visible child has at least a first name
    for (let i = 0; i < children.length; i += 1) {
      if (!children[i].first_name?.trim()) {
        toast.error(`Please enter a name for child ${i + 1}.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        num_children: String(formData.num_children),
        // Keep child_first_name / child_age for backward compatibility
        child_first_name: children[0].first_name,
        child_age: children[0].age,
        children: children.map((c) => ({ first_name: c.first_name, age: c.age })),
      };
      await axios.post(`${API}/public/mini-makers-waitlist`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="min-h-screen"
        style={{ backgroundColor: COLORS.cream, fontFamily: "'Nunito', 'Quicksand', system-ui, sans-serif" }}
        data-testid="mini-makers-page"
      >
        {/* HERO */}
        <header
          className="relative overflow-hidden"
          style={{
            background: `linear-gradient(180deg, ${COLORS.purple} 0%, ${COLORS.purpleDeep} 100%)`,
          }}
          data-testid="mini-makers-header"
        >
          {/* playful confetti dots */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            {[...Array(18)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${6 + (i % 4) * 4}px`,
                  height: `${6 + (i % 4) * 4}px`,
                  background: [COLORS.gold, COLORS.orange, COLORS.pink, COLORS.green, COLORS.teal][i % 5],
                  top: `${(i * 53) % 100}%`,
                  left: `${(i * 37) % 100}%`,
                }}
              />
            ))}
          </div>

          <div className="relative max-w-6xl mx-auto px-6 py-10 sm:py-14">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center lg:justify-start"
              >
                <img
                  src={LOGO_URL}
                  alt="Mini Makers Market Camp"
                  className="w-full max-w-md drop-shadow-2xl"
                  data-testid="mini-makers-logo"
                />
              </motion.div>

              {/* Hero text */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-center lg:text-left text-white"
              >
                <span
                  className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                  style={{ backgroundColor: COLORS.gold, color: COLORS.purpleDeep }}
                >
                  A Creative Summer Experience
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">
                  Where kids turn{" "}
                  <span style={{ color: COLORS.gold }}>ideas</span> into real products,
                  build their own <span style={{ color: COLORS.pink }}>brands</span>, and
                  showcase them like <span style={{ color: COLORS.gold }}>pros!</span>
                </h1>
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                  style={{ backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)" }}
                >
                  <Star className="w-4 h-4" style={{ color: COLORS.gold }} fill={COLORS.gold} />
                  <span className="text-sm font-bold tracking-wide">Boys & Girls · Ages 4–16</span>
                </div>
                <div>
                  <button
                    onClick={scrollToForm}
                    className="px-8 py-4 rounded-full font-extrabold text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.purpleDeep }}
                    data-testid="hero-join-waitlist-btn"
                  >
                    Join the Priority Waitlist →
                  </button>
                </div>
                <p className="mt-4 text-sm text-white/70">
                  Be the first to know when registration opens.
                </p>
              </motion.div>
            </div>
          </div>
        </header>

        {/* CAMPERS WILL */}
        <section className="py-16 px-6" data-testid="campers-will-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span
                className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white"
                style={{ backgroundColor: COLORS.purple }}
              >
                Campers Will
              </span>
              <h2 className="text-3xl sm:text-4xl font-black" style={{ color: COLORS.purpleDeep }}>
                Create. Brand. Showcase. Sell.
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
              {CAMPER_STEPS.map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-3xl p-5 sm:p-6 text-center shadow-md border-2 hover:shadow-xl hover:-translate-y-1 transition-all"
                  style={{ borderColor: `${step.color}40` }}
                  data-testid={`camper-step-${i}`}
                >
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md"
                    style={{ backgroundColor: step.color }}
                  >
                    <step.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-base font-extrabold mb-1.5" style={{ color: step.color }}>
                    {step.title.toUpperCase()}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-snug">{step.sub}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* INTRO BLURB */}
        <section className="py-12 px-6" style={{ backgroundColor: "white" }} data-testid="intro-section">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              Mini Makers Market Camp is a{" "}
              <span className="font-bold" style={{ color: COLORS.purple }}>
                Mix & Mingle Events
              </span>{" "}
              experience designed for young creators, future entrepreneurs, and curious kids who love to make things with
              their hands. Children explore creativity, product-making, branding, confidence, presentation
              skills, and the joy of showcasing their work in a mini market-style environment.
            </p>
          </div>
        </section>

        {/* CAMP HIGHLIGHTS + DETAIL TILES */}
        <section className="py-16 px-6" data-testid="highlights-section">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
            {/* Highlights card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl p-8 shadow-lg relative overflow-hidden"
              style={{ backgroundColor: COLORS.purple, color: "white" }}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20" style={{ backgroundColor: COLORS.gold }} />
              <h3 className="text-2xl sm:text-3xl font-black mb-6 flex items-center gap-3">
                <Sparkles className="w-7 h-7" style={{ color: COLORS.gold }} />
                Camp Highlights
              </h3>
              <ul className="space-y-3">
                {HIGHLIGHTS.map((h, i) => (
                  <li key={h} className="flex items-start gap-3" data-testid={`highlight-${i}`}>
                    <div
                      className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: COLORS.gold }}
                    >
                      <Check className="w-4 h-4" style={{ color: COLORS.purpleDeep }} strokeWidth={3} />
                    </div>
                    <span className="text-base sm:text-lg font-semibold">{h}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Detail tiles */}
            <div className="grid grid-cols-2 gap-4">
              {DETAIL_TILES.map((tile, i) => (
                <motion.div
                  key={tile.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl p-5 shadow-md border-2"
                  style={{ borderColor: `${tile.color}30` }}
                  data-testid={`detail-tile-${tile.label.toLowerCase()}`}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${tile.color}18` }}
                  >
                    <tile.icon className="w-6 h-6" style={{ color: tile.color }} />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: tile.color }}>
                    {tile.label}
                  </p>
                  <p className="text-2xl font-black mt-1" style={{ color: COLORS.purpleDeep }}>
                    {tile.value}
                  </p>
                  <p className="text-xs text-gray-500 mt-1.5 leading-snug">{tile.note}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WAITLIST FORM */}
        <section
          id="waitlist-form"
          className="py-16 px-6"
          style={{ backgroundColor: "white" }}
          data-testid="mini-makers-form-section"
        >
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
                data-testid="mini-makers-success"
              >
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <Check className="w-12 h-12 text-white" strokeWidth={3} />
                </div>
                <h3 className="text-3xl sm:text-4xl font-black mb-3" style={{ color: COLORS.purpleDeep }}>
                  You're on the list!
                </h3>
                <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
                  We'll send you the details as soon as Mini Makers Market Camp registration opens.
                </p>
                <div
                  className="rounded-2xl p-6 max-w-lg mx-auto"
                  style={{ backgroundColor: `${COLORS.gold}20`, border: `2px solid ${COLORS.gold}60` }}
                >
                  <p className="text-gray-800 leading-relaxed">
                    Thank you for joining the priority waitlist. Keep an eye on your inbox — we'll share
                    dates, venue, pricing, and registration details with you first.
                  </p>
                </div>
              </motion.div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <span
                    className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white"
                    style={{ backgroundColor: COLORS.pink }}
                  >
                    Join the Priority Waitlist
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ color: COLORS.purpleDeep }}>
                    Limited spots. Maximum impact.
                  </h2>
                  <p className="text-gray-600">
                    Be the first to know when registration opens for our creative summer camp.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-5 rounded-3xl p-6 sm:p-8 shadow-lg"
                  style={{ backgroundColor: COLORS.cream, border: `2px solid ${COLORS.purple}20` }}
                  data-testid="mini-makers-form"
                >
                  {/* Parent Name */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">
                        Parent / Guardian First Name *
                      </label>
                      <input
                        type="text"
                        name="parent_first_name"
                        value={formData.parent_first_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                        data-testid="mm-parent-first"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Parent / Guardian Last Name</label>
                      <input
                        type="text"
                        name="parent_last_name"
                        value={formData.parent_last_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                        data-testid="mm-parent-last"
                      />
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                        data-testid="mm-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                        data-testid="mm-phone"
                      />
                    </div>
                  </div>

                  {/* How many children? */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      How many children would you like to register? *
                    </label>
                    <select
                      name="num_children"
                      value={formData.num_children}
                      onChange={handleNumChildrenChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                      data-testid="mm-num-children"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "child" : "children"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Child Info — dynamic */}
                  <div className="space-y-3" data-testid="mm-children-list">
                    {children.map((child, i) => (
                      <div
                        key={i}
                        className="grid sm:grid-cols-[1fr_180px] gap-3 sm:gap-4 rounded-2xl p-3 sm:p-4"
                        style={{ backgroundColor: "white", border: `1px solid ${COLORS.purple}15` }}
                        data-testid={`mm-child-row-${i}`}
                      >
                        <div>
                          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" style={{ color: COLORS.purple }}>
                            Child {i + 1} · First Name {i === 0 ? "*" : ""}
                          </label>
                          <input
                            type="text"
                            value={child.first_name}
                            onChange={(e) => handleChildChange(i, "first_name", e.target.value)}
                            required={i === 0}
                            placeholder={`Child ${i + 1}'s first name`}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-[#FAF6EC] text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all placeholder:text-gray-400"
                            data-testid={`mm-child-name-${i}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" style={{ color: COLORS.purple }}>
                            Age
                          </label>
                          <select
                            value={child.age}
                            onChange={(e) => handleChildChange(i, "age", e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-[#FAF6EC] text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
                            data-testid={`mm-child-age-${i}`}
                          >
                            <option value="">Select age</option>
                            {[4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((a) => (
                              <option key={a} value={a}>
                                {a} years old
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Preferred Location / Area</label>
                    <input
                      type="text"
                      name="preferred_location"
                      value={formData.preferred_location}
                      onChange={handleChange}
                      placeholder="e.g. South Edmonton, St. Albert, Sherwood Park…"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all placeholder:text-gray-400"
                      data-testid="mm-location"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Any questions or notes?</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all resize-none placeholder:text-gray-400"
                      placeholder="Anything you'd like us to know…"
                      data-testid="mm-notes"
                    />
                  </div>

                  {/* Consent */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="agree_updates"
                      checked={formData.agree_updates}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded border-gray-300"
                      style={{ accentColor: COLORS.purple }}
                      data-testid="mm-consent"
                    />
                    <label className="text-sm text-gray-700 leading-relaxed">
                      I agree to receive updates about Mini Makers Market Camp from Mix & Mingle Events. *
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-full font-extrabold text-base sm:text-lg text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: COLORS.purple }}
                    data-testid="mm-submit-btn"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting…
                      </span>
                    ) : (
                      "Join the Waitlist →"
                    )}
                  </button>
                </form>

                <p className="text-center text-gray-500 text-xs mt-6 leading-relaxed max-w-lg mx-auto">
                  This is not a registration form yet. This priority waitlist helps us notify interested
                  families first when the date, venue, pricing, and full registration details are released.
                </p>
              </>
            )}
          </div>
        </section>

        {/* FOOTER */}
        <footer
          className="py-12 px-6"
          style={{ backgroundColor: COLORS.purpleDeep, color: "white" }}
          data-testid="mini-makers-footer"
        >
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/60 text-sm mb-1">Presented by</p>
            <p className="font-black text-2xl mb-1">Mix & Mingle Events</p>
            <p className="text-white/70 text-sm italic mb-6">Create. Connect. Celebrate.</p>
            <p className="text-white/50 text-xs">
              hello@mixandmingleevents.com · www.mixandmingleevents.com
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MiniMakersWaitlist;
